import { useMemo } from "react";
import { DOCUMENTATION_CONTENT } from "./data/resourcesData";
import ResourcesTOC from "./components/ResourcesTOC";
import WorkflowDiagram from "./components/WorkflowDiagram";
import ResourceDemoCTA from "./components/ResourceDemoCTA";
import ResourceDemoPreview from "./components/ResourceDemoPreview";
import ResourceFeatureImage from "./components/ResourceFeatureImage";
import styles from "./ResourcesLayout.module.css";

/**
 * ResourcesPage — Single reusable content renderer for all documentation pages.
 *
 * Props:
 *   section: string — Key into DOCUMENTATION_CONTENT (e.g. 'overview', 'interactive-3d')
 *
 * Renders all content blocks (paragraph, heading, list, workflow, divider, callout)
 * from the static data. Also provides heading data to ResourcesTOC for the
 * "On this page" navigation.
 */
const ResourcesPage = ({ section }) => {
  const content = DOCUMENTATION_CONTENT[section];

  // Extract headings for TOC
  const headings = useMemo(() => {
    if (!content?.sections) return [];
    return content.sections
      .filter((s) => s.type === "heading" && s.id)
      .map((s) => ({ id: s.id, text: s.text, level: s.level }));
  }, [content]);

  if (!content) {
    return (
      <div className={styles["docs-content-wrapper"]}>
        <article className={styles["docs-content"]}>
          <h1 className={styles["docs-page-title"]}>Page Not Found</h1>
          <p className={styles["docs-paragraph"]}>
            The requested documentation page could not be found.
          </p>
        </article>
      </div>
    );
  }

  return (
    <div className={styles["docs-content-wrapper"]}>
      <article className={styles["docs-content"]} id="resources-content">
        {/* Page header */}
        <header className={styles["docs-page-header"]}>
          <h1 className={styles["docs-page-title"]}>{content.title}</h1>
          {content.subtitle && (
            <p className={styles["docs-page-subtitle"]}>{content.subtitle}</p>
          )}
        </header>

        {/* Content sections */}
        <div className={styles["docs-body"]}>
          {content.sections.map((block, idx) => (
            <ContentBlock key={idx} block={block} section={section} />
          ))}
        </div>
      </article>

      {/* Right-side TOC */}
      <ResourcesTOC headings={headings} />
    </div>
  );
};

/**
 * ContentBlock — Renders a single content block based on its type.
 */
function ContentBlock({ block, section }) {
  switch (block.type) {
    case "heading":
      return block.level === 2 ? (
        <h2 className={styles["docs-h2"]} id={block.id}>
          {block.text}
        </h2>
      ) : (
        <h3 className={styles["docs-h3"]} id={block.id}>
          {block.text}
        </h3>
      );

    case "paragraph":
      return <p className={styles["docs-paragraph"]}>{block.text}</p>;

    case "list":
      if (block.ordered) {
        return (
          <ol className={styles["docs-list-ordered"]}>
            {block.items.map((item, i) => (
              <li key={i} className={styles["docs-list-item"]}>
                {item}
              </li>
            ))}
          </ol>
        );
      }
      return (
        <ul className={styles["docs-list"]}>
          {block.items.map((item, i) => (
            <li key={i} className={styles["docs-list-item"]}>
              {item}
            </li>
          ))}
        </ul>
      );

    case "workflow":
      return <WorkflowDiagram steps={block.steps} label={block.label} />;

    case "divider":
      return <hr className={styles["docs-divider"]} />;

    case "callout":
      return (
        <div
          className={`${styles["docs-callout"]} ${
            block.variant === "tip"
              ? styles["docs-callout-tip"]
              : styles["docs-callout-info"]
          }`}
        >
          {block.text}
        </div>
      );

    case "demo_cta":
      const projectType =
        section === "interactive-3d" ? "unityScene" :
        section === "ar-experiences" ? "webUrl" :
        section === "360-tours" ? "360Tour" : null;

      const featureImage =
        section === "access-control" ? { src: "/assets/rbac_illustration.jpg", alt: "User role and permission management interface" } :
        section === "publish-controls" ? { src: "/assets/publish_controls_illustration.jpg", alt: "Content publishing and visibility controls" } :
        section === "instant-updates" ? { src: "/assets/instant_updates_illustration.jpg", alt: "Content update and publishing interface" } : null;

      return (
        <>
          {projectType && <ResourceDemoPreview projectType={projectType} />}
          {featureImage && <ResourceFeatureImage imageSrc={featureImage.src} alt={featureImage.alt} />}
          <ResourceDemoCTA
            title={block.title}
            description={block.description}
            primaryLabel={block.primaryLabel}
            secondaryLabel={block.secondaryLabel}
          />
        </>
      );

    default:
      return null;
  }
}

export default ResourcesPage;
